import { formatDate, formatDateTime } from '../common/util/datetime';
import { latestAssignment } from './devices';
import brand from '../shell/brand';
import { exportCsv } from './csv';

// Each register as a spreadsheet.
//
// The vehicle list has been downloadable for a while and the rest were not, which made the answer to
// "send me the driver list" a screenshot. One description per register, written once: what the
// columns are called and where each value comes from.
//
// The columns are the ones on screen rather than the fields underneath them. A register is read by
// people who recognise a plate and a carrier's name, not an id, so an assignment exports the truck
// and the device by name and the milliseconds come out as minutes - the same translations the page
// makes. Ids are included where somebody might have to match this back to another system.

const minutes = (ms) => (ms > 0 ? Math.round(ms / 60000) : '');
const yesNo = (value) => (value ? 'Yes' : 'No');

// kind -> the columns, each a header and a way to read it from a row. `ctx` carries the rest of the
// registers, because a row that points at another one is exported by that one's name.
export const EXPORTS = {
  drivers: [
    ['Driver', (row) => row.name],
    ['Reference', (row) => row.reference],
    ['Mobile', (row) => row.mobile || ''],
    ['Added', (row) => formatDate(row.createdAt, '')],
  ],
  transporters: [
    ['Transporter', (row) => row.name],
    ['Contact', (row) => row.contactName || ''],
    ['Contact mobile', (row) => row.contactMobile || ''],
    ['GSTIN', (row) => row.gstin || ''],
    ['Added', (row) => formatDate(row.createdAt, '')],
  ],
  sources: [
    ['Device', (row) => row.identifier],
    ['Type', (row) => row.kind],
    ['Status', (row) => row.status || 'ACTIVE'],
    ['Site', (row) => row.site || ''],
    ['SIM', (row) => row.simNumber || ''],
    ['Model', (row) => row.model || ''],
    ['Tracking reference', (row) => row.providerId || ''],
    // What the register's "Current use" column says, in the words it says it in.
    ['Current use', (row, ctx) => {
      const latest = latestAssignment(row.id, ctx.assignments);
      const vehicle = (id) => ctx.vehicles.find((v) => v.id === id)?.name || 'a vehicle';
      if (latest && !latest.endedAt) return `On ${vehicle(latest.vehicleId)} · ${latest.mode === 'PERMANENT' ? 'Permanent' : 'Temporary'}`;
      if (latest?.mode === 'TEMPORARY' && !latest.returnedAt && !['LOST', 'RETIRED'].includes(row.status || 'ACTIVE')) return `Awaiting return from ${vehicle(latest.vehicleId)}`;
      return (row.status || 'ACTIVE') === 'ACTIVE' ? 'Available' : 'Cannot be issued';
    }],
    ['Last fix', (row) => formatDateTime(row.lastFixAt, '')],
  ],
  assignments: [
    ['Vehicle', (row, ctx) => ctx.name('vehicles', row.vehicleId)],
    ['Device', (row, ctx) => ctx.name('sources', row.sourceId)],
    ['Device type', (row) => row.kind],
    ['Attachment', (row) => row.mode],
    ['State', (row) => (row.returnedAt ? 'Returned' : row.endedAt ? 'Detached' : 'Active')],
    ['Attached', (row) => formatDateTime(row.startedAt, '')],
    ['Detached', (row) => formatDateTime(row.endedAt, '')],
    ['Returned', (row) => formatDateTime(row.returnedAt, '')],
  ],
  checkpoints: [
    ['Checkpoint', (row) => row.name],
    ['Site', (row) => row.site],
    ['Stage', (row) => row.type],
    ['Traffic', (row) => row.handles || 'BOTH'],
    ['Dwell threshold (min)', (row) => minutes(row.dwellMs)],
    ['Geofence', (row) => (row.shape === 'POLYLINE' ? `Lane · ${(row.path || []).length} points · ${Math.round((row.corridorMeters || 0) * 2)} m wide` : row.radiusMeters > 0 ? `Circle · ${row.radiusMeters} m` : 'None')],
    ['Latitude', (row) => row.latitude ?? ''],
    ['Longitude', (row) => row.longitude ?? ''],
  ],
  routes: [
    ['Route', (row) => row.name],
    ['Stages', (row, ctx) => (row.checkpointIds || []).map((id) => ctx.name('checkpoints', id)).join(' → ')],
    ['Next-milestone timeout (min)', (row) => minutes(row.transitMs)],
  ],
  readers: [
    ['Reader', (row) => row.name],
    ['Identifier', (row) => row.identifier],
    ['Checkpoint', (row, ctx) => ctx.name('checkpoints', row.checkpointId)],
    ['Model', (row) => row.model || ''],
    ['Health', (row) => (row.health || 'NEVER_SEEN').replaceAll('_', ' ')],
    ['Offline after (s)', (row) => row.heartbeatSeconds],
    ['Last heartbeat', (row) => formatDateTime(row.lastSeenAt, '')],
    ['Last tag read', (row) => formatDateTime(row.lastReadAt, '')],
  ],
  catalog: [
    ['List', (row) => row.list],
    ['Name', (row) => row.name],
    ['Learned from a trip', (row) => yesNo(row.learned)],
  ],
};

export const downloadable = (kind) => Object.prototype.hasOwnProperty.call(EXPORTS, kind);

/**
 * Writes the rows currently on screen, in the order they are on screen.
 *
 * What is downloaded is what the person is looking at - a search or a filter has already narrowed
 * it - because a file that quietly holds more than the screen did is a file nobody can reconcile.
 */
export const downloadRegistry = (kind, rows, data = {}) => {
  const columns = EXPORTS[kind];
  if (!columns) return;
  const ctx = {
    vehicles: data.vehicles || [],
    assignments: data.assignments || [],
    name: (of, id) => {
      const found = (data[of] || []).find((row) => row.id === id);
      return found?.name || found?.identifier || '';
    },
  };
  const headers = columns.map(([header]) => header);
  exportCsv(`${brand.key}-${kind}`, headers, rows.map((row) => Object.fromEntries(
    columns.map(([header, read]) => [header, read(row, ctx)]),
  )));
};
